// A label's size, the one picker every React screen uses (Mitch, 2026-11-04: "the w / h fields should always be
// separate. So it's 3 fields: width, height, unit (default mm). Then one dropdown on the right that has the pre-defined
// sizes, which fills in those fields. Changing a value in one of the fields makes the selection set to custom", and "the
// same exact shared module code … a simple 'rotate' option as well to flip the height/width"): width, height, unit and
// Rotate, then the presets. Its math is shared/label-size.ts, which the vendors' documents page uses too. Users: the
// vendor portal's label picker and a label part's size (PackagingSection).
import { RotateCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fromMm, type LabelPreset, type LabelUnit, type MmSize, presetKey, rotated, sameSize, toMm } from "../../../shared/label-size.ts";
import { cx, Select } from "./controls.tsx";

export type { MmSize } from "../../../shared/label-size.ts";
export type LabelSizeText = { width: string; height: string; unit: string; presets: string; custom: string; choose: string; rotate: string };
export const LABEL_SIZE_TEXT: LabelSizeText = {
  width: "Width",
  height: "Height",
  unit: "Unit",
  presets: "Label sizes",
  custom: "Custom",
  choose: "Pick a size",
  rotate: "Rotate",
};

/**
 * `size` in mm (null: not set); `onSize` gets a size from 10 to 400 mm a side: as it's typed (`live`, a preview) or once
 * a field is left (saving). A preset fills both fields; Rotate swaps them.
 */
export function LabelSizeFields({
  size,
  presets,
  onSize,
  live = false,
  disabled,
  text = LABEL_SIZE_TEXT,
  onInvalid,
  className,
}: {
  size: MmSize | null;
  presets: readonly LabelPreset[];
  onSize: (s: MmSize) => void;
  live?: boolean;
  disabled?: boolean;
  text?: LabelSizeText;
  /** A size off the limits, when a field is left. */
  onInvalid?: () => void;
  className?: string;
}) {
  const [unit, setUnit] = useState<LabelUnit>("mm");
  const [w, setW] = useState(fromMm(size?.w, unit));
  const [h, setH] = useState(fromMm(size?.h, unit));
  // What it last said (its own changes don't come back into the fields while they're being typed in)
  const sent = useRef<MmSize | null>(size);
  const [sw, sh] = [size?.w ?? null, size?.h ?? null];
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a size from outside (its numbers) refills the fields
  useEffect(() => {
    if (sent.current && sw != null && sh != null && sent.current.w === sw && sent.current.h === sh) return;
    sent.current = sw != null && sh != null ? { w: sw, h: sh } : null;
    setW(fromMm(sw, unit));
    setH(fromMm(sh, unit));
  }, [sw, sh]);
  const say = (next: MmSize) => {
    sent.current = next;
    if (!size || size.w !== next.w || size.h !== next.h) onSize(next);
  };
  const commit = (wt: string, ht: string) => {
    const [a, b] = [toMm(wt, unit), toMm(ht, unit)];
    if (a == null || b == null) return false;
    say({ w: a, h: b });
    return true;
  };
  const leave = () => {
    if (live || commit(w, h)) return;
    if (w.trim() || h.trim()) onInvalid?.();
    setW(fromMm(size?.w, unit));
    setH(fromMm(size?.h, unit));
  };
  const fill = (s: MmSize) => {
    setW(fromMm(s.w, unit));
    setH(fromMm(s.h, unit));
    say(s);
  };
  const [mw, mh] = [toMm(w, unit), toMm(h, unit)];
  const current = mw != null && mh != null ? { w: mw, h: mh } : null;
  const preset = current ? presets.find((p) => sameSize(p, current)) : undefined;
  const box =
    "h-7 w-16 rounded-md border border-line bg-surface px-1.5 text-right font-mono text-[12px] text-text outline-none focus:border-accent disabled:opacity-60";
  const field = (label: string, value: string, set: (v: string) => void, other: string, isW: boolean) => (
    <label className="flex items-center gap-1 text-[12px] text-muted">
      {label}
      <input
        inputMode="decimal"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          set(e.target.value);
          if (live) commit(isW ? e.target.value : other, isW ? other : e.target.value);
        }}
        onBlur={leave}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        className={box}
      />
    </label>
  );
  return (
    <div className={cx("flex flex-wrap items-center gap-1.5", className)}>
      {field(text.width, w, setW, h, true)}
      <span className="text-faint">×</span>
      {field(text.height, h, setH, w, false)}
      <div className="w-16 shrink-0">
        <Select
          aria-label={text.unit}
          value={unit}
          disabled={disabled}
          options={[
            { value: "mm", label: "mm" },
            { value: "in", label: "in" },
          ]}
          onChange={(e) => {
            const next = e.target.value as LabelUnit;
            const [a, b] = [toMm(w, unit), toMm(h, unit)];
            setUnit(next);
            if (a != null) setW(fromMm(a, next));
            if (b != null) setH(fromMm(b, next));
          }}
          className="h-7 text-[12px]"
        />
      </div>
      <button
        type="button"
        disabled={disabled || !current || current.w === current.h}
        onClick={() => current && fill(rotated(current))}
        title={current ? `${text.rotate}: ${fromMm(current.h, unit)} × ${fromMm(current.w, unit)} ${unit}` : text.rotate}
        className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-line bg-surface px-2 text-[12px] text-muted hover:bg-hover hover:text-text disabled:opacity-50"
      >
        <RotateCw className="size-3.5" />
        {text.rotate}
      </button>
      <div className="ml-auto min-w-40 flex-1">
        <Select
          aria-label={text.presets}
          value={preset ? presetKey(preset) : current ? "custom" : ""}
          disabled={disabled}
          options={[
            ...(current ? [] : [{ value: "", label: text.choose }]),
            ...presets.map((p) => ({ value: presetKey(p), label: p.label })),
            { value: "custom", label: text.custom },
          ]}
          onChange={(e) => {
            const p = presets.find((x) => presetKey(x) === e.target.value);
            if (p) fill({ w: p.w, h: p.h });
          }}
          className="h-7 text-[12px]"
        />
      </div>
    </div>
  );
}
